import Disabled from "../../common/Disabled";
import IconButton from "../../common/IconButton";
import SmallLoader from "../../common/SmallLoader";
import Visible from "../../common/Visible";
import DownloadOriginalButton from "../../common/files/DownloadOriginalButton";
import CleanMicrosoftFileButton from "./CleanMicrosoftFileButton";
import { DRAG_N_DROP_TYPE_DIR, DRAG_N_DROP_TYPE_FILE } from "./DirListTreeItem";
import EditableFilename from "./EditableFilename";
import ProcessLossRunButton from "./ProcessLossRunButton";
import ProcessSicCodesButton from "./ProcessSicCodesButton";
import { useFocusInGuardRef } from "./useFocusInGuardRef";
import Block from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningIcon from "@mui/icons-material/WarningRounded";
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { TreeItem } from "@mui/x-tree-view";
import { hasItems } from "common/arrays";
import { formatDate } from "common/dates";
import HiddenIfReadOnly from "components/common/HiddenIfReadOnly";
import React from "react";
import { useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import * as staticDataService from "services/staticDataService";

const Blank = () => <div style={{ width: "40px" }}>&nbsp;</div>;

export const PreviewButton = ({ support, ...props }) => {
  return support.previewable() ? (
    <div style={{ width: "40px" }}>
      <IconButton
        icon={VisibilityIcon}
        onClick={() => {
          window.open(
            "/attachments/fileView?" +
              new URLSearchParams({
                fileId: support.getFileId(),
                filename: support.filename(),
                mime: support.mimeType(),
                type: support.previewable(),
              }),
            "_blank"
          );
        }}
        {...props}
      />
    </div>
  ) : support.isHtmlNotAvailable() || support.hasHtmlLoadFailed() ? (
    <Blank />
  ) : (
    <SmallLoader />
  );
};

export const DeleteButton = ({ support, ...props }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setConfirmOpen(true);
  };

  const handleConfirmDelete = (e) => {
    e?.stopPropagation();
    setConfirmOpen(false);
    support.markAttachmentHidden();
  };

  const handleCancelDelete = (e) => {
    e?.stopPropagation();
    setConfirmOpen(false);
  };

  if (support.canHide()) {
    if (support.isFileLoading()) {
      return <SmallLoader />;
    }
    return (
      <>
        <Disabled ifReadOnly>
          <IconButton
            icon={DeleteIcon}
            onClick={handleDeleteClick}
            {...props}
          />
        </Disabled>
        <Dialog
          open={confirmOpen}
          onClose={handleCancelDelete}
          onClick={(e) => e.stopPropagation()}
          aria-labelledby={"delete-file-dialog-title"}
          aria-describedby={"delete-file-dialog-description"}
        >
          <DialogTitle id={"delete-file-dialog-title"}>
            {"Delete Attachment?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id={"delete-file-dialog-description"}>
              {`Are you sure you want to delete "${support.filename()}"? This action cannot be undone.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color={"primary"}>
              {"Cancel"}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color={"error"}
              variant={"contained"}
              autoFocus
            >
              {"Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
  return <Blank />;
};

const CleanButton = ({ support }) => {
  const stat = support.microsoftFileCleanStatus();
  return ["NOT_READABLE", "NOT_AVAILABLE"].includes(stat) ? (
    <Blank />
  ) : stat === "CLEANED" ? (
    <DoneIcon style={{ width: "40px" }} />
  ) : stat === "CLEANING" ? (
    <SmallLoader />
  ) : (
    <Disabled ifReadOnly>
      <CleanMicrosoftFileButton support={support} />
    </Disabled>
  );
};

const DownloadButton = ({ support, ...props }) => {
  if (support.isFileLoadingFailed()) {
    return <Blank />;
  }
  if (support.isFileLoaded()) {
    return (
      <DownloadOriginalButton
        sourceMetadata={support.metadata()}
        smallButton={true}
        newName={support.filename()}
        {...props}
      />
    );
  }
  return <SmallLoader />;
};

const FileAvatar = ({ support }) => {
  const theme = useTheme();
  const colours = support.getColours();
  const Icon = support.getIcon();
  const avatar = (
    <Avatar
      variant={"rounded"}
      sx={{
        bgcolor: colours.background,
        width: "30px",
        height: "30px",
      }}
    >
      <Icon support={support} color={colours.primary} />
    </Avatar>
  );

  let resp = avatar;

  let tooltip = null;
  if (support.isLegacyMicrosoftFile()) {
    tooltip = `'${support.filename()}' is of a legacy file format`;
    resp = (
      <Box sx={{ position: "relative" }}>
        {resp}
        <Box
          sx={{
            position: "absolute",
            bottom: "-4px",
            right: "-5px",
            width: "16px",
            height: "16px",
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <WarningIcon
            fontSize={"0.75rem"}
            sx={{ color: theme.brand.gray.text }}
          />
        </Box>
      </Box>
    );
  }
  if (support.isNotReadable()) {
    if (tooltip) {
      tooltip += " and is read protected";
    } else {
      tooltip = `'${support.filename()}' is read protected`;
    }
    resp = (
      <Box sx={{ position: "relative" }}>
        {resp}
        <Box
          sx={{
            position: "absolute",
            bottom: "-4px",
            left: "-2px",
            width: "16px",
            height: "16px",
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Block fontSize={"0.75rem"} sx={{ color: "#ff5555" }} />
        </Box>
      </Box>
    );
  }
  if (tooltip) {
    resp = <Tooltip title={tooltip}>{resp}</Tooltip>;
  }
  return resp;
};

const ProcessButtons = ({ support }) => {
  return support.canProcessSicCodes() || support.canProcessLossRun() ? (
    <HiddenIfReadOnly>
      <Stack direction={"row"} gap={1}>
        <Visible byTag={"input.attachment.sic"}>
          <ProcessSicCodesButton support={support} />
        </Visible>
        <Visible byTag={"input.attachment.lossRun"} defaultVisible>
          <ProcessLossRunButton support={support} />
        </Visible>
      </Stack>
    </HiddenIfReadOnly>
  ) : (
    <></>
  );
};

const hasMetadataInfo = (support, documentTypes) => {
  const meta = support.metadata();
  return (
    hasItems(documentTypes) ||
    (meta?.creationDate || meta?.createdBy) != null ||
    support.documentTypes?.[support.getFileId()] != null
  );
};

const FileMetadataInfo = ({ support }) => {
  const { data: configData } = staticDataService.useConfigQuery(
    "attachment_document_types"
  );
  const documentTypes = configData?.attachmentDocumentTypes;

  if (!hasMetadataInfo(support, documentTypes)) {
    return null;
  }

  const dateAdded = support.metadata()?.creationDate ?? null;
  const dateAddedLabel = formatDate(dateAdded);
  const addedBy = support.metadata()?.createdBy ?? null;
  const attachmentDocumentType =
    support.documentTypes?.[support.getFileId()] ?? null;

  const handleDocTypeChange = (e) => {
    e.stopPropagation();
    support.setAttachmentDocumentType(e.target.value || null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        ml: "38px",
      }}
    >
      {dateAddedLabel && (
        <Typography variant={"caption"} color={"text.secondary"}>
          {dateAddedLabel}
        </Typography>
      )}
      {addedBy && (
        <Typography variant={"caption"} color={"text.secondary"}>
          {addedBy}
        </Typography>
      )}
      {hasItems(documentTypes) && (
        <Select
          size={"small"}
          value={attachmentDocumentType || ""}
          onChange={handleDocTypeChange}
          onClick={(e) => e.stopPropagation()}
          displayEmpty
          variant={"standard"}
          sx={{ fontSize: "0.75rem", minWidth: 100 }}
        >
          <MenuItem value={""}>
            <em>{"Type"}</em>
          </MenuItem>
          {documentTypes.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );
};

const FileListTreeItem = ({
  support,
  createFileButtons,
  selected,
  setSelected,
}) => {
  const [isEditing, setEditing] = useState(false);
  const [, dragRef] = useDrag({
    type: DRAG_N_DROP_TYPE_FILE,
    item: () => ({
      type: DRAG_N_DROP_TYPE_FILE,
      dirId: support.getDirId(),
      fileId: support.getFileId(),
    }),
    canDrag: (monitor) => support.isFileLoaded() && !isEditing,
    end: (item, monitor) => {
      const dropRes = monitor.getDropResult();
      if (dropRes) {
        const selectedIds = selected.map((support) => support.getUniqueId());
        if (selectedIds.includes(support.getUniqueId())) {
          for (const currentSupport of selected) {
            if (currentSupport.isDir) {
              currentSupport.moveDir(dropRes.dir);
            } else {
              if (dropRes.type === DRAG_N_DROP_TYPE_DIR)
                currentSupport.moveFileToDirectory(dropRes.dir);
              else {
                currentSupport.moveFileToFile(dropRes.dir);
              }
            }
          }
        } else {
          if (dropRes.type === DRAG_N_DROP_TYPE_DIR)
            support.moveFileToDirectory(dropRes.dir);
          else {
            support.moveFileToFile(dropRes.dir);
          }
        }
      }
      setSelected([]);
    },
  });

  const [dropProps, dropRef] = useDrop({
    accept: [DRAG_N_DROP_TYPE_FILE],
    drop: (item, monitor) => {
      return {
        type: DRAG_N_DROP_TYPE_FILE,
        fileId: support.getFileId(),
        dir: support.getDirId(),
      };
    },
    canDrop: (item, monitor) => {
      const has = support.hasFile(item.fileId);
      return has && item.fileId !== support.getFileId();
    },
    collect: (monitor) => ({
      highlighted: monitor.canDrop() && monitor.isOver(),
    }),
  });

  const dragFix = useFocusInGuardRef();

  const dragDropRef = (el) => {
    dragRef(el);
    dropRef(el);
  };
  const itemButtons = [
    createFileButtons
      ? createFileButtons({ support })
      : [
          <ProcessButtons key={"processButtons"} {...{ support }} />,
          <CleanButton support={support} key={"fix-button"} />,
          <PreviewButton
            support={support}
            key={"preview"}
            data-testid={`preview-file-${support.filename()}`}
          />,
          <DeleteButton
            support={support}
            key={"delete"}
            data-testid={`delete-file-${support.filename()}`}
          />,
          <DownloadButton
            support={support}
            key={"download"}
            data-testid={`download-file-${support.filename()}`}
          />,
        ],
  ];
  const dropStyleProps = dropProps.highlighted
    ? {
        boxShadow: "0 0 0 4px inset orange",
        borderRadius: "6px",
      }
    : {};
  return (
    <TreeItem
      ref={dragFix}
      key={support.getUniqueId()}
      itemId={support.getUniqueId()}
      label={
        <Box style={dropStyleProps} sx={{ p: 0.5, pr: 0 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <div
              ref={dragDropRef}
              style={{
                flexGrow: 2,
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ mr: 1 }}>
                  {support.isFileExpanding() ? (
                    <SmallLoader />
                  ) : (
                    <FileAvatar support={support} />
                  )}
                </Box>
                <EditableFilename
                  value={support.filename()}
                  extension={support.ext()}
                  onSubmit={(newName) => support.setAttachmentName(newName)}
                  id={`editable-filename-${support.getFileId()}`}
                  uploaded={support.isFileLoaded()}
                  style={{ fontWeight: "inherit", flexGrow: 1 }}
                  noEditing={!support.isFilenameEditable()}
                  onEdit={() => {
                    setEditing(true);
                  }}
                  onClose={() => {
                    setEditing(false);
                  }}
                />
              </Box>
              <FileMetadataInfo support={support} />
            </div>
            {itemButtons}
          </Box>
        </Box>
      }
    >
      {support.children
        .map((child, index) => {
          return child.fileHidden() ? null : (
            <FileListTreeItem
              support={child}
              createFileButtons={createFileButtons}
              selected={selected}
              setSelected={setSelected}
              key={`FILE_${support.getDirId()}-${support.getFileId()}_${index}`}
            />
          );
        })
        .filter((node) => node)}
    </TreeItem>
  );
};

export default FileListTreeItem;
